/* The kinds built out of labelled boxes: a prompt and the answer to it, a
   worked example, a weak version beside a strong one, a numbered explainer.

   They share a file because they share the one rule that matters here: **a
   prompt and a sample of the answer are two different kinds of thing and are
   set as two different surfaces** — a framed box in the typewriter voice for
   what is pasted, a dashed and muted one in the reading voice for what came
   back. A reader must never have to work out which of the two they are looking
   at, and 247 prompts in the material is 247 chances to get that wrong once.

   OWNED BY ITEM 3 OF docs/plan/00-prestavba-2026-08.md. */

import { Box, Copy, Head, Notes, T } from "./shared";
import { revealed, useReveal } from "../routes/deck/reveal";
import { useI18n } from "../lib/i18n";
import { setModel, useModel } from "../lib/model";
import { PROMPT_MODELS } from "../lib/types";
import type { CompareBlock, ExampleBlock, PromptBlock, PromptSet, StepsBlock } from "../lib/types";

export function Prompt({ b }: { b: PromptBlock }) {
  return (
    <>
      <Head b={b} />
      <Box text={b.prompt} cls="prompt" label={b.promptLabel} dflt="block.prompt" copy />
      <Box text={b.out} cls="out" label={b.outLabel} dflt="block.out" />
      <Notes b={b} />
    </>
  );
}

/* A worked example end to end: what was asked, what went in, the prompt, what
   came out. Four surfaces in the order the work happened in. */
export function Example({ b }: { b: ExampleBlock }) {
  return (
    <>
      <Head b={b} />
      <Box text={b.task} cls="prose" />
      <Box text={b.input} cls="out" dflt="block.input" />
      <Box text={b.prompt} cls="prompt" dflt="block.prompt" copy />
      <Box text={b.out} cls="out" dflt="block.out" />
      <Notes b={b} />
    </>
  );
}

/* Weak beside strong. Each column carries its own heading, so the boxes inside
   it are the ones that go without a label. */
export function Compare({ b }: { b: CompareBlock }) {
  return (
    <>
      <Head b={b} />
      <div className="compare">
        {[b.bad, b.good].map((s, i) => (
          <div key={i} className={"side " + (i ? "good" : "bad")}>
            <h4><T value={s.label} /></h4>
            <Box text={s.text} cls="prompt" label={null} copy />
            <Box text={s.out} cls="out" dflt="block.out" />
          </div>
        ))}
      </div>
    </>
  );
}

/* A numbered explainer. An item may be held back on its own, so the list can be
   walked one line at a time in front of a room. */
export function Steps({ b }: { b: StepsBlock }) {
  const at = useReveal();
  return (
    <>
      <Head b={b} />
      <ol className="steps">
        {b.steps.map((s, i) => (
          <li key={i} data-step={s.s} className={revealed(s.s, at) ? "on" : undefined}>
            <div className="t"><T value={s.t} /></div>
            {s.d ? <div className="d"><T value={s.d} /></div> : null}
          </li>
        ))}
      </ol>
      <Box text={b.prompt} cls="prompt" dflt="block.prompt" copy />
      <Notes b={b} />
    </>
  );
}


/* One prompt, three tabs.

   `core` is the whole prompt; a per-model field holds only what that model
   needs said differently and is appended under a blank line. What the reader
   sees is what the button hands over — the composed text, ready to paste — and
   they never assemble anything themselves.

   Where a model needs nothing said differently the tab shows the core alone and
   says so. A tab that changes nothing and admits nothing reads as a page that
   is broken; a tab that says "nothing changes here" is an answer.

   The choice of model is shared by every prompt on the page — see lib/model.ts.

   OWNED BY ITEM 4 OF PLAN.md. */
export function ModelPrompt({ prompt }: { prompt: PromptSet }) {
  const { t, ui } = useI18n();
  const model = useModel();

  const core = t(prompt.core);
  const delta = t(prompt[model]);
  const text = delta ? `${core}\n\n${delta}` : core;

  return (
    <div className="pset">
      <div className="box-label">
        {/* The label of a kit prompt is a file name — instructions.md,
            design.md — and a file name is not translated, so it must not
            wear the "not translated" mark on /en the way prose would. */}
        {prompt.label ? t(prompt.label) : ui("lesson.prompt")}
      </div>
      <div className="pset-tabs" role="group" aria-label={ui("prompts.model")}>
        {PROMPT_MODELS.map((m) => (
          <button key={m} type="button" aria-pressed={m === model}
                  className={"pset-tab" + (m === model ? " on" : "")}
                  onClick={() => setModel(m)}>
            {ui(`model.${m}`)}
          </button>
        ))}
      </div>
      <div className="prompt"><Copy text={text} />{text}</div>
      {delta ? null : <p className="pset-same">{ui("prompts.noDelta")}</p>}
    </div>
  );
}
